import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm'; // Pour le support des tables, listes de tâches, etc.
import remarkMath from 'remark-math'; // Pour la syntaxe mathématique (LaTeX) comme $...$ et $$...$$
import rehypeKatex from 'rehype-katex'; // Pour le rendu des maths avec KaTeX
import 'katex/dist/katex.min.css'; // Le CSS de KaTeX est nécessaire pour un bon rendu

// ── Spoilers en ligne de style Discord (||spoiler||) ──────────

function InlineSpoiler({ children }) {
    const [revealed, setRevealed] = useState(false);

    return (
        <span
            onClick={(e) => {
                e.stopPropagation();
                setRevealed(true);
            }}
            className={`inline rounded px-1.5 py-0.5 transition-all duration-200 cursor-pointer select-none font-semibold ${
                revealed
                    ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 cursor-default'
                    : 'bg-gray-950 dark:bg-gray-700 text-transparent selection:bg-transparent select-none hover:bg-gray-900 dark:hover:bg-gray-600'
            }`}
            title={revealed ? '' : 'Cliquez pour dévoiler'}
        >
            {children}
        </span>
    );
}

function groupSpoilers(children) {
    if (!children) return children;
    const array = Array.isArray(children) ? children : [children];
    const newChildren = [];
    let insideSpoiler = false;
    let currentSpoilerChildren = [];

    for (let i = 0; i < array.length; i++) {
        const child = array[i];

        if (typeof child === 'string') {
            if (child.includes('||')) {
                const parts = child.split('||');
                for (let j = 0; j < parts.length; j++) {
                    const part = parts[j];
                    if (j === 0) {
                        if (insideSpoiler) {
                            if (part) currentSpoilerChildren.push(part);
                            newChildren.push(
                                <InlineSpoiler key={`spoiler-${i}-${j}`}>
                                    {currentSpoilerChildren}
                                </InlineSpoiler>
                            );
                            currentSpoilerChildren = [];
                            insideSpoiler = false;
                        } else {
                            if (part) newChildren.push(part);
                        }
                    } else {
                        // For subsequent parts: they alternate between inside and outside
                        if (insideSpoiler) {
                            if (part) currentSpoilerChildren.push(part);
                            newChildren.push(
                                <InlineSpoiler key={`spoiler-${i}-${j}`}>
                                    {currentSpoilerChildren}
                                </InlineSpoiler>
                            );
                            currentSpoilerChildren = [];
                            insideSpoiler = false;
                        } else {
                            // Start new spoiler
                            insideSpoiler = true;
                            currentSpoilerChildren = [];
                            if (part) currentSpoilerChildren.push(part);
                        }
                    }
                }
            } else {
                if (insideSpoiler) {
                    currentSpoilerChildren.push(child);
                } else {
                    newChildren.push(child);
                }
            }
        } else {
            // It's a React element or another object
            if (insideSpoiler) {
                currentSpoilerChildren.push(child);
            } else {
                newChildren.push(child);
            }
        }
    }

    // Flush any unclosed spoiler
    if (insideSpoiler && currentSpoilerChildren.length > 0) {
        newChildren.push(
            <InlineSpoiler key="spoiler-unclosed">
                {currentSpoilerChildren}
            </InlineSpoiler>
        );
    }

    return newChildren.length === 1 ? newChildren[0] : newChildren;
}

export const renderContent = (text) => {
    if (!text) return null;
    if (typeof text !== 'string') return text.toString(); // Gérer les cas où le contenu n'est pas une chaîne
    
    const mediaRegex = /(<svg\b[^>]*>[\s\S]*?<\/svg>|<img\b[^>]*\/?>)/gi;
    const parts = text.split(mediaRegex);
    
    return parts.map((part, index) => {
        if (!part) return null;

        if (part.toLowerCase().startsWith('<svg') || part.toLowerCase().startsWith('<img')) {
            return (
                <div 
                    key={index} 
                    className="flex justify-center items-center my-4 w-full max-w-full rounded-xl [&>svg]:w-full md:[&>svg]:w-auto [&>svg]:max-w-full [&>svg]:h-auto [&>img]:w-full md:[&>img]:w-auto [&>img]:max-w-full [&>img]:h-auto" 
                    dangerouslySetInnerHTML={{ __html: part }} 
                />
            );
        } else {
            // Pour les parties de texte, utiliser ReactMarkdown avec le support LaTeX et Spoilers
            return (
                <ReactMarkdown
                    key={index}
                    remarkPlugins={[remarkGfm, remarkMath]}
                    rehypePlugins={[rehypeKatex]}
                    components={{
                        // Surcharge du rendu des paragraphes pour éviter les marges par défaut et injecter les spoilers
                        p: ({ node, children, ...props }) => <p className="mb-0" {...props}>{groupSpoilers(children)}</p>,
                        // Ajout d'un style de base pour les listes
                        ul: ({ node, children, ...props }) => <ul className="list-disc list-inside ml-4" {...props}>{children}</ul>,
                        ol: ({ node, children, ...props }) => <ol className="list-decimal list-inside ml-4" {...props}>{children}</ol>,
                        li: ({ node, children, ...props }) => <li className="mb-1" {...props}>{groupSpoilers(children)}</li>,
                        td: ({ node, children, ...props }) => <td {...props}>{groupSpoilers(children)}</td>,
                        th: ({ node, children, ...props }) => <th {...props}>{groupSpoilers(children)}</th>,
                        h1: ({ node, children, ...props }) => <h1 {...props}>{groupSpoilers(children)}</h1>,
                        h2: ({ node, children, ...props }) => <h2 {...props}>{groupSpoilers(children)}</h2>,
                        h3: ({ node, children, ...props }) => <h3 {...props}>{groupSpoilers(children)}</h3>,
                        h4: ({ node, children, ...props }) => <h4 {...props}>{groupSpoilers(children)}</h4>,
                        h5: ({ node, children, ...props }) => <h5 {...props}>{groupSpoilers(children)}</h5>,
                        h6: ({ node, children, ...props }) => <h6 {...props}>{groupSpoilers(children)}</h6>,
                    }}
                >
                    {part}
                </ReactMarkdown>
            );
        }
    });
};